import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  ChatChannelStateType,
  FetchChatChannelListPayloadType,
} from 'features/Account/@types/chatChannel'
import { callErrorMsg } from 'helpers/errorMsg'
import { getChatChannelList } from '../../services/chatChannel'

const PAGE_LIMIT = 20

const STATUS_PARAM = {
  all: undefined,
  active: 'open',
  done: 'closed',
} as const

const initialState: ChatChannelStateType = {
  isLoading: false,
  isLoadingMore: false,
  requestId: '',
  channels: [],
  nextCursor: '',
  hasNextPage: false,
}

export const SLICE_NAME = 'chatChannel'

const chatChannelSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchChatChannelList.pending, (state, action) => {
        const { isLoadMore = false } = action.meta.arg
        state.requestId = action.meta.requestId
        state.isLoading = !isLoadMore
        state.isLoadingMore = isLoadMore
      })
      .addCase(fetchChatChannelList.rejected, (state, action) => {
        // a newer request has replaced this one
        if (state.requestId !== action.meta.requestId) return

        state.isLoading = false
        state.isLoadingMore = false
      })
      .addCase(fetchChatChannelList.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return

        const { data, pagination } = action.payload
        state.isLoading = false
        state.isLoadingMore = false
        state.channels = action.meta.arg.isLoadMore ? [...state.channels, ...data] : data
        state.nextCursor = pagination.next_cursor
        state.hasNextPage = pagination.next_page_exist
      })
  },
})

export default chatChannelSlice.reducer
export const { reset } = chatChannelSlice.actions

export const fetchChatChannelList = createAsyncThunk(
  `${SLICE_NAME}/fetchChatChannelList`,
  async (
    { orderId, phone, status, isLoadMore = false }: FetchChatChannelListPayloadType,
    { rejectWithValue, getState },
  ) => {
    const {
      chatChannel: { nextCursor },
    } = getState() as StoreStateType

    try {
      const res = await getChatChannelList({
        params: {
          limit: PAGE_LIMIT,
          direction: 'next',
          cursor: isLoadMore ? nextCursor : undefined,
          order_id: orderId || undefined,
          phone: phone || undefined,
          status: STATUS_PARAM[status],
        },
      })
      return res.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
